import React, {useState, useEffect} from "react"

import {injectIntl, FormattedMessage, InjectIntl} from "@irati/utils/lang"
import Hr from "@irati/ui/src/Layout/Hr"
import {ButtonRouterLink, RouterLink} from "@irati/ui/src/Link"
import Flex from "@irati/ui/src/Flex"
import {Body, Label} from "@irati/ui/src/Typography"
import {Team, TeamState} from "@irati/types"
import adminTracking from "@irati-tracking/admin"
import {useLazyTeamsPaginated} from "@irati/identity/hooks"
import authentication from "@irati/auth"
import scrollAtBottom from "@irati/utils/scroll/scrollAtBottom"
import Loading from "@irati/ui/src/Loading"
import {hangPromise} from "@irati/utils/promise"
import {getAllTeams} from "@irati/identity/helpers/teams/getAllTeamsForTenant"

import AdminHeader from "../../AdminHeader/AdminHeader"
import {ADD_TEAM, ANALYTICS_PERKS, SETTINGS_PERMISSIONS_RECOGNITION, ASSIGN_MANAGERS, UPDATE_TEAMS} from "../../../routes/routeConfig"
import NoTeams from "../NoTeams/NoTeams"
import TeamRow from "./Team"
import MinWidth from "./styled/MinWidth"
import AssignManagersModal from "./AssignManagersModal"

interface Props extends InjectIntl {
  setCurrentTeam: (team: Team) => void
}

const TeamList = ({setCurrentTeam, formatMessage}: Props) => {
  const [loading, setLoading] = useState<Promise<any> | null>(hangPromise())
  const [allTeams, setAllTeams] = useState<Team[]>([])
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedTeam, setSelectedTeam] = useState<Team>()
  const [searchAfter, setSearchAfter] = useState(null)
  const [cursor, setCursor] = useState(null)
  const [apollo] = useLazyTeamsPaginated(
    {
      businessId: authentication.business().uuid,
      limit: 20,
      state: TeamState.Generic,
      searchAfter
    },
    {fetchPolicy: "network-only"}
  )
  useEffect(() => {
    getAllTeams().then((teams) => {
      setLoading(null)
      setAllTeams(teams)
    })
  }, [])

  useEffect(() => {
    if (apollo?.data) {
      setCursor(apollo?.data.searchAfter)
      setAllTeams([...allTeams, ...apollo?.data.teams])
      setLoading(null)
    }
  }, [apollo?.data])

  useEffect(() => {
    if (apollo.error) {
      setLoading(null)
    }
  }, [apollo.error])

  useEffect(() => {
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [cursor])

  const teamsNoMagagers = allTeams?.filter((t: Team) => t.managers?.total === 0).length

  const infoCount =
    allTeams?.length > 1 ? "admin.components.teamList.nameCountAndButton.count" : "admin.components.teamList.nameCountAndButton.count2"
  const errorCount =
    teamsNoMagagers > 1
      ? "admin.components.teamList.nameCountAndButton.errorCount"
      : "admin.components.teamList.nameCountAndButton.errorCount2"
  const successCount =
    allTeams?.length > 1
      ? "admin.components.teamList.nameCountAndButton.successCount"
      : "admin.components.teamList.nameCountAndButton.successCount2"

  const handleScroll = () => {
    const atBottom = scrollAtBottom()
    const {error} = apollo

    if (atBottom && !error) {
      getNextPage()
    }
  }

  const getNextPage = () => {
    if (cursor && cursor !== searchAfter) {
      setSearchAfter(cursor)
    }
  }

  const handleAssignManagers = () => {
    adminTracking.bulkAssignManagers()
  }

  const setTeamManagers = (team: Team) => {
    setSelectedTeam(team)
    setShowModal(true)
  }

  const handleOnSave = (count: number) => {
    setShowModal(false)
    const updatedTeams = allTeams.map((item) => {
      if (item.uuid === selectedTeam?.uuid) {
        return {...item, managers: {...item.managers, total: count}}
      }
      return item
    })
    setAllTeams(updatedTeams)
  }

  return (
    <Loading load={loading}>
      {allTeams?.length === 0 ? (
        <NoTeams />
      ) : (
        <>
          <Flex alignItems="flex-start" justifyContent="space-between">
            <Flex direction="column" alignItems="flex-start">
              <AdminHeader
                infoCount={formatMessage({id: infoCount}, {count: allTeams?.length})}
                errorCount={teamsNoMagagers !== 0 && formatMessage({id: errorCount}, {count: teamsNoMagagers})}
                successCount={teamsNoMagagers === 0 && formatMessage({id: successCount}, {count: allTeams?.length})}
                title={formatMessage({id: "admin.components.teamList.nameCountAndButton.title"})}
                marginBottom={0.5}
              />
              <Body small marginBottom={2}>
                <Body small marginBottom={0.5}>
                  <FormattedMessage id="admin.components.teamList.header.paragraph1" />
                </Body>
                <Body small marginBottom={1}>
                  <FormattedMessage
                    id="admin.components.teamList.header.paragraph2"
                    values={{
                      report: (
                        <RouterLink primary to={ANALYTICS_PERKS}>
                          <FormattedMessage id="admin.components.teamList.header.reportLink" />
                        </RouterLink>
                      ),
                      permissions: (
                        <RouterLink primary to={SETTINGS_PERMISSIONS_RECOGNITION}>
                          <FormattedMessage id="admin.components.teamList.header.permissionsLink" />
                        </RouterLink>
                      )
                    }}
                  />
                </Body>
              </Body>
            </Flex>
            <Flex alignItems="center" justifyContent="flex-end" wrap="wrap" gap="8px 16px">
              <ButtonRouterLink small to={UPDATE_TEAMS} id="admin_teams_buttonUpdateTeams">
                <FormattedMessage id="admin.components.teamList.bulkUpdateTeamsButton" />
              </ButtonRouterLink>
              <ButtonRouterLink small primary to={ADD_TEAM} id="admin_teams_buttonCreateTeam">
                <FormattedMessage id="admin.components.teamList.nameCountAndButton.buttonText" />
              </ButtonRouterLink>
              <ButtonRouterLink small primary to={ASSIGN_MANAGERS} id="admin_teams_buttonAssignManagers" onClick={handleAssignManagers}>
                <FormattedMessage id="admin.manageUsers.button.assignManagers" />
              </ButtonRouterLink>
            </Flex>
          </Flex>
          <Hr />
          <Flex>
            <MinWidth>
              <Label large weight="bold">
                {formatMessage({id: "admin.components.teamList.smallCaption.text"})}
              </Label>
            </MinWidth>
            <Flex width="100%" alignItems="center">
              <MinWidth>
                <Label large weight="bold">
                  {formatMessage({id: "admin.components.teamList.teamType.text"})}
                </Label>
              </MinWidth>
              <MinWidth>
                <Label large weight="bold">
                  {formatMessage({id: "admin.components.teamList.activePeople.text"})}
                </Label>
              </MinWidth>
              <MinWidth>
                <Label large weight="bold">
                  {formatMessage({id: "admin.components.teamList.managers.text"})}
                </Label>
              </MinWidth>
            </Flex>
            <Flex justifyContent="flex-end" width="400px" />
          </Flex>
          {allTeams &&
            allTeams.map((team) => (
              <TeamRow
                key={team.uuid}
                team={team}
                {...team}
                setCurrentTeam={() => setCurrentTeam(team)}
                buttonText="platform.button.edit"
                setTeamManagers={() => setTeamManagers(team)}
              />
            ))}
          <AssignManagersModal show={showModal} teamId={selectedTeam?.uuid} onHide={() => setShowModal(false)} onSave={handleOnSave} />
        </>
      )}
    </Loading>
  )
}

export default injectIntl(TeamList)
